import React, { SetStateAction, useState, Dispatch, ChangeEvent, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Badge,
  Radio,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { LatLng, LeafletMouseEvent } from 'leaflet';
import MapMarker from '../MapMarker';
import { useStyles } from './styles';
import { Coordinates } from '../../pages/AddEngineering';
import ShowSnackbar, { SetSnackbarType } from '../SnackBar';
import api, { googleApi } from '../../services/api';
import { Intervencao } from '../../@types/engineeringTypes';
import {
  TipoIntervencaoList,
  EspecificacaoOp,
  TipoElementoList,
} from '../../pages/AddEngineering/utils';
import SubInputWarn from '../SubInputWarn';
import {
  GoogleMapsReverseResponseType,
  GoogleMapsSearchResponseType,
} from '../../@types/geocodingTypes';
import AutoCompleteField from '../AutoCompleteVirtualized';
import { viaNames } from '../LocalizationFormGroup/viaNames';
import { simNaoNaoInformado } from '../../utils/objectsSelect';
interface IProps {
  intervention: Intervencao;
  setIntervention: Dispatch<SetStateAction<Intervencao>>;
  latlng: Coordinates;
  setLatLng: Dispatch<SetStateAction<Coordinates>>;
  formErrors: { key: string; message: string }[];
  setFormErrors: React.Dispatch<SetStateAction<{ key: string; message: string }[]>>;
  adresses: { address1: string; address2: string; neighborhood: string };
  setAdresses: React.Dispatch<
    React.SetStateAction<{ address1: string; address2: string; neighborhood: string }>
  >;
  setIntersection: React.Dispatch<React.SetStateAction<'Sim' | 'Não' | 'Não informado'>>;
  intersection: string;
  setReference: React.Dispatch<React.SetStateAction<string>>;
  reference: string;
  polylines?: Array<LatLng>;
  setPolylines?: React.Dispatch<React.SetStateAction<Array<LatLng>>>;
}

const EnginneringTopForm: React.FC<IProps> = ({
  intervention,
  setIntervention,
  latlng,
  setLatLng,
  polylines,
  setPolylines,
  formErrors,
  setFormErrors,
  adresses,
  setAdresses,
  intersection,
  setIntersection,
  setReference,
  reference,
}) => {
  const [address1, setAddress1] = useState<string>(adresses.address1);
  const [address2, setAddress2] = useState<string>(adresses.address2);
  const [snackbar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [searchText, setSearchText] = useState<string>('');
  const [locationText, setLocationText] = useState<string>('');
  const [geoLoading, setGeoLoading] = useState<boolean>(false);
  const [fieldSpecs, setFieldSpecs] = useState<string[]>([
    'Ciclovia',
    'Ciclofaixa Unidirecional',
    'Ciclofaixa Unidirecional com Segregador',
    'Ciclofaixa Bidirecional',
    'Ciclofaixa Bidirecional com Segregador',
    'Ciclorrota',
  ]);
  const [errors, setErrors] = useState<{ key: string; message: string }[]>([]);
  const classes = useStyles();
  const geocodeKey = `${process.env.REACT_APP_GMAPS_API_KEY}`;

  const handleCoordinates = async (event: ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = Number(event.target.value);
    if (value) {
      setLatLng({ ...latlng, [name]: value });
    }
  };

  const getAddress = async (event: LeafletMouseEvent) => {
    setGeoLoading(true);
    setLatLng({ lat: event.latlng.lat, lng: event.latlng.lng });
    setSearchText('');
    try {
      const { data } = await googleApi.get<GoogleMapsReverseResponseType>(
        `/api/geocode/json?latlng=${event.latlng.lat},${event.latlng.lng}&key=${geocodeKey}`,
      );
      const address = data.results[0];

      setLocationText(
        `${
          address.address_components[1].long_name ||
          address.address_components[0].long_name
        } ${`${address.address_components[0].short_name} - ` || ''}${
          address.address_components[2].short_name || ''
        }`,
      );
      setAdresses({
        address1: `${address.address_components[1].long_name}`,
        address2: '',
        neighborhood: address.address_components[2].long_name,
      });
      setReference(`${address.address_components[0].short_name}`);
      setGeoLoading(false);
    } catch {
      setGeoLoading(false);
      setLocationText('');
      setSnackbar({
        open: true,
        message: 'Não foi posível realizar a geolocalização. Tente novamente.',
      });
      setAddress1('');
      setReference('');
    }
  };

  const handleChange = (key: string, value: string) => {
    setIntervention({ ...intervention, [key]: value });
  };

  const getGeolocation = async () => {
    setGeoLoading(true);
    try {
      let treatString = searchText.replace(/[\u0020-\u002F]/g, ' ');
      treatString = treatString.replace(/\s/g, ' ');
      const { data } = await googleApi.get<GoogleMapsSearchResponseType>(
        `/api/geocode/json?address=${treatString}&language=pt-BR&key=${geocodeKey}`,
      );

      if (data.status !== 'OK') {
        setGeoLoading(false);
        setSnackbar({
          open: true,
          message: 'Não foi possível encontrar o endereço informado.',
        });
        return;
      }
      const address = data.results[0];

      setLocationText(
        `${
          address.address_components[1].long_name ||
          address.address_components[0].long_name
        } ${`${address.address_components[0].short_name} - ` || ''}${
          address.address_components[2].short_name || ''
        }`,
      );

      setLatLng({
        lat: address.geometry.location.lat,
        lng: address.geometry.location.lng,
      });

      setGeoLoading(false);
    } catch {
      setGeoLoading(false);
      setSnackbar({
        open: true,
        message: 'Não foi possível encontrar o endereço informado.',
      });
    }
  };

  const changeSpec = (key: string) => {
    const spec = EspecificacaoOp.find((item) => item.key === key);
    if (spec) {
      setFieldSpecs(spec.labels);
      setIntervention((prev) => {
        return { ...prev, Especificação: spec.labels[0] };
      });
      return;
    }
    setIntervention((prev) => {
      return { ...prev, Especificação: '' };
    });
  };

  useEffect(() => {
    setIntervention(intervention);
    setErrors(formErrors);
  }, [intervention, formErrors]);

  return (
    <Box className={classes.sectionContainer}>
      <Typography variant="h6">Tipo da Intervenção</Typography>
      <Grid container xl={12} spacing={2}>
        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label}>Tipo da Intervenção</InputLabel>
          <Select
            variant="outlined"
            className={classes.select}
            value={intervention.Tipo}
            onChange={(event) => {
              handleChange('Tipo', event.target.value as string);
              changeSpec(event.target.value as string);
            }}
            onBlur={() => setIntervention(intervention)}
          >
            {TipoIntervencaoList.map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label}>Especifícação</InputLabel>
          <Select
            variant="outlined"
            className={classes.select}
            value={intervention.Especificação || fieldSpecs[0]}
            onChange={(event) => {
              handleChange('Especificação', event.target.value as string);
            }}
            onBlur={() => setIntervention(intervention)}
          >
            {fieldSpecs.map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label}>Tipo de Elemento</InputLabel>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={intervention.Elemento}
            onChange={(event) => {
              handleChange('Elemento', event.target.value as string);
              changeSpec(event.target.value as string);
            }}
            row
          >
            <FormControlLabel
              value="Ponto"
              control={<Radio color="primary" />}
              label="Ponto"
            />
            <FormControlLabel
              value="Linha"
              control={<Radio color="primary" />}
              label="Linha"
            />
          </RadioGroup>
        </Grid>
      </Grid>

      <Grid container spacing={2} xs={12} className={classes.searchBar}>
        <Grid item sm={12} md={10} xs={12}>
          <InputLabel className={classes.label}>Pesquisar endereço</InputLabel>
          <TextField
            name="Pesquisar"
            placeholder="Rua, nº (opcional), bairro, cidade, estado"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            variant="outlined"
            size="small"
            className={classes.textField}
          />
        </Grid>

        <Grid item sm={2} xs={2}>
          <Button
            className={classes.buttonSearch}
            variant="contained"
            color="primary"
            onClick={getGeolocation}
            disabled={!searchText}
          >
            Pesquisar
          </Button>
        </Grid>
      </Grid>

      <Grid item xl={12}>
        <Badge
          color="secondary"
          badgeContent=" "
          invisible={!errors.filter((item) => item.key === 'location').length}
          style={{
            margin: '0 0 5px 0',
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            border: `${
              errors.filter((item) => item.key === 'location').length
                ? '1px solid red'
                : '1px solid transparent'
            }`,
          }}
        >
          <div className={classes.mapContainer}>
            <MapMarker
              onClick={getAddress}
              point={[latlng.lat, latlng.lng]}
              interventionType={intervention.Elemento}
              location={polylines}
              setLocation={setPolylines}
            />
            <div className={classes.mapFeedback}>
              {geoLoading ? (
                <CircularProgress size={23} thickness={5} />
              ) : (
                <Typography variant="subtitle2" style={{ fontWeight: 'normal' }}>
                  {locationText}
                </Typography>
              )}
            </div>
          </div>
        </Badge>
        <SubInputWarn
          type="error"
          formWarns={formErrors.filter((item) => item.key === 'location')}
        />
      </Grid>
      <Grid container spacing={3} md={12} xs={12}>
        <Grid item sm={12} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Endereço 1
          </InputLabel>
          <TextField
            type="text"
            placeholder="Informe o endereço 1"
            onChange={(event) => setAddress1(event.target.value)}
            onBlur={() =>
              setAdresses((prev) => {
                return { ...prev, address1 };
              })
            }
            value={adresses.address1}
            variant="outlined"
            size="small"
            className={`${classes.textField}
                }`}
          />
          <SubInputWarn
            type="error"
            formWarns={formErrors.filter((item) => item.key === 'address1')}
          />
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Cruzamento
          </InputLabel>
          <Select
            name="Cruzamento"
            placeholder="Cruzamento"
            value={intersection}
            onChange={(event) => {
              if (event.target.value === intersection) return null;
              return setIntersection(
                event.target.value as 'Sim' | 'Não' | 'Não informado',
              );
            }}
            className={classes.select}
            variant="outlined"
          >
            {simNaoNaoInformado.map((item) => (
              <MenuItem value={item.label} key={item.label}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Endereço 2
          </InputLabel>
          <TextField
            type="text"
            disabled={intersection !== 'Sim'}
            placeholder="Informe o endereço 2"
            onChange={(event) => setAddress2(event.target.value)}
            onBlur={() =>
              setAdresses((prev) => {
                return { ...prev, address2 };
              })
            }
            value={intersection !== 'Sim' ? '' : address2}
            variant="outlined"
            size="small"
            className={`${classes.textField}
                }`}
          />
          <SubInputWarn
            type="error"
            formWarns={formErrors.filter((item) => item.key === 'address2')}
          />
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Número ou Referência
          </InputLabel>
          <TextField
            type="text"
            placeholder="Informe um número de endereço ou ponto de referência"
            onChange={(event) => setReference(event.target.value)}
            value={reference}
            variant="outlined"
            size="small"
            className={`${classes.textField}
                }`}
          />
        </Grid>
      </Grid>
      {polylines && polylines?.length > 1 ? (
        polylines.map((loc: LatLng, index: number) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.label}>Latitude {index + 1}</InputLabel>
              <TextField
                name="lat"
                type="number"
                placeholder="Latitude"
                onChange={handleCoordinates}
                value={loc.lat}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.label}>Longitude {index + 1}</InputLabel>
              <TextField
                name="lng"
                placeholder="Longitude"
                onChange={handleCoordinates}
                value={loc.lng}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </Grid>
          </Grid>
        ))
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InputLabel className={classes.label}>Latitude</InputLabel>
            <TextField
              name="lat"
              type="number"
              placeholder="Latitude"
              onChange={handleCoordinates}
              value={latlng.lat}
              variant="outlined"
              size="small"
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel className={classes.label}>Longitude</InputLabel>
            <TextField
              name="lng"
              placeholder="Longitude"
              onChange={handleCoordinates}
              value={latlng.lng}
              variant="outlined"
              size="small"
              className={classes.textField}
            />
          </Grid>
        </Grid>
      )}
      <ShowSnackbar
        open={snackbar.open}
        message={snackbar.message}
        setSnackbar={setSnackbar}
      />
    </Box>
  );
};

export default EnginneringTopForm;
